import React, { useState } from 'react';
import MDBox from 'components/MDBox';
import { Badge, IconButton, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import MDTypography from 'components/MDTypography';
import moment from 'moment/moment';
import { gpsStatusBadgeColor } from 'utils/constant';
import { withStyles } from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import { renderIcon } from 'utils/renderIcon';
import {
    columnVisibilityModelAssetTracker,
    columnVisibilityModelHolyKel,
    columnVisibilityModelLevelMonitor,
    columnVisibilityModelTablet
} from './constants';
import MDButton from 'components/MDButton';
import ColumnSettingsPanel from './ColumnSettingsPanel';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LinkedDeviceDetailPanel from './LinkedDeviceDetailPanel';
import { formatUllageNumber, ULLAGE_ESTIMATE_TOOLTIP } from 'utils/ullage';

const DashboardDataTable = ({ rowData, selectedRows, setSelectedRows, showAlert, selectedDeviceType, searchQuery, onSearch, qrModel, onChangeQrModel }) => {
    const StyledLinearProgress = withStyles({
        root: {
            height: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 10px",
            position: "relative",
            borderRadius: "15px",
        },
        bar: {
            borderRadius: "5px",
            backgroundColor: '#1a90ff',
        },
    })(LinearProgress);
    const [columnVisibilityAssetTracker, setColumnVisibilityAssetTracker] = useState(localStorage.getItem('columnVisibilityAssetTracker') ? JSON.parse(localStorage.getItem('columnVisibilityAssetTracker')) : columnVisibilityModelAssetTracker)
    const [columnVisibilityLevelMonitor, setColumnVisibilityLevelMonitor] = useState(localStorage.getItem('columnVisibilityLevelMonitor') ? JSON.parse(localStorage.getItem('columnVisibilityLevelMonitor')) : columnVisibilityModelLevelMonitor)
    const [columnVisibilityHolyKel, setColumnVisibilityHolyKel] = useState(localStorage.getItem('columnVisibilityHolyKel') ? JSON.parse(localStorage.getItem('columnVisibilityHolyKel')) : columnVisibilityModelHolyKel)
    const [columnVisibilityTablet, setColumnVisibilityTablet] = useState(localStorage.getItem('columnVisibilityTablet') ? JSON.parse(localStorage.getItem('columnVisibilityTablet')) : columnVisibilityModelTablet)

    // Column order state management
    const [columnOrderAssetTracker, setColumnOrderAssetTracker] = useState(() => {
        const saved = localStorage.getItem('columnOrderAssetTracker');
        return saved ? JSON.parse(saved) : [];
    });
    const [columnOrderLevelMonitor, setColumnOrderLevelMonitor] = useState(() => {
        const saved = localStorage.getItem('columnOrderLevelMonitor');
        return saved ? JSON.parse(saved) : [];
    });
    const [columnOrderHolyKel, setColumnOrderHolyKel] = useState(() => {
        const saved = localStorage.getItem('columnOrderHolyKel');
        return saved ? JSON.parse(saved) : [];
    });
    const [columnOrderTablet, setColumnOrderTablet] = useState(() => {
        const saved = localStorage.getItem('columnOrderTablet');
        return saved ? JSON.parse(saved) : [];
    });

    // Column settings panel state
    const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);

    // Linked device expansion state
    const [expandedRows, setExpandedRows] = useState({});

    const redirectUrl = (e, value) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event from bubbling
        window.open(`/devices/view-device/${value}`, '_blank')
    }

    const assetTrackerColumns = [
        {
            field: "_expand",
            headerName: " ",
            width: 44,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const row = params.row;
                if (!row.has_linked_device) return null;
                const isExpanded = expandedRows[row.id];
                const handleClick = (e) => {
                    e.stopPropagation();
                    setExpandedRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
                };
                return (
                    <Tooltip title={isExpanded ? 'Collapse linked devices' : 'Expand linked devices'}>
                        <IconButton size="small" onClick={handleClick} sx={{ color: '#fff' }}>
                            {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "convert_id",
            headerName: "ID",
            width: 50,
            renderCell: (info) => {
                return <a className='a-href' style={{ cursor: 'pointer' }} onClick={(e) => redirectUrl(e, info.value)} target="_blank" rel="noopener noreferrer">
                    {info.value}
                </a>
            },
        },
        {
            field: "alert",
            headerName: "Alert",
            headerAlign: 'center',
            align: 'center',
            renderCell: (info) => {
                return info.value ? (<i
                    title={info.value?.description}
                    className={info.value?.icon?.class_name ?? "fas fa-triangle-exclamation"}
                    onClick={(e) => showAlert(e, info.id)}
                    style={{ cursor: 'pointer', color: `${info.value?.badge?.font_color_hex ?? 'red'}`, fontSize: '1.5em' }}
                />
                ) : ''
            },
        },
        {
            field: "moment_status",
            headerName: "Status",
            renderCell: (info) => {
                return <Badge
                    color={gpsStatusBadgeColor(info.value)}
                    variant={'standard'}
                    badgeContent={info.value}
                    style={{ marginLeft: "15px" }}
                />
            },
        },
        {
            field: "moment_status_duration",
            headerName: "Duration",
            renderCell: (info) => {
                let dur = moment.duration({
                    from: info.value,
                    to: new Date()
                });
                return <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                    {dur.humanize()}
                </MDTypography>
            }
        },
        {
            field: "name",
            headerName: "Device Name",
            width: 200,
        },
        {
            field: "identifier",
            headerName: "Identifier",
            width: 160
        },
        {
            field: "speed",
            headerName: "Speed",
        },
        {
            field: "battery_level",
            headerName: "Batt. Level",
        },
        {
            field: "external_powersource_status",
            headerName: "Ext. Power",
        },
        {
            field: "engine_ignition_status",
            headerName: "Ignition",
        },
        {
            field: "fuel_level",
            headerName: "Fuel Level",
        },
        {
            field: "rpm",
            headerName: "RPM",
        },
        {
            field: "location",
            headerName: "Location",
            width: 300,
            renderCell: ({ value, row }) => {
                return (
                    value ? (
                        <MDTypography variant="span" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                            {value}
                            <div style={{ width: '15px', height: '15px', borderRadius: '50%', display: 'inline-block', backgroundColor: row.color }} ></div>
                        </MDTypography>
                    ) : row.should_show_address ? (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row.should_show_address}
                        </MDTypography>
                    ) : ''
                );
            }
        },
        {
            field: "entity",
            headerName: "Entity Name",
            width: 200
        },
        {
            field: "entity_address_name",
            headerName: "Item Name",
            width: 200
        },
        {
            field: "item",
            headerName: "Item Type",
            width: 150
        },
        {
            field: "address",
            headerName: "Address",
            width: 200
        },
        {
            field: "city",
            headerName: "City",
            width: 150
        },
        {
            field: "state",
        },
        {
            field: "postal_code",
            headerName: "Postal Code",
        },
        {
            field: "lat",
            headerName: "Latitude",
            width: 110
        },
        {
            field: "long",
            headerName: "Longitude",
            width: 110
        },
        {
            field: 'pressure_psi',
            headerName: 'Pressure (PSI)',
            width: 140,
            renderCell: ({ row }) => (
                <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                    {row?.linked_devices?.[0]?.device_value?.channels?.[10]?.current_value ?? ''}
                </MDTypography>
            )
        },
        {
            field: 'level_percent',
            headerName: 'Level (%)',
            width: 120,
            renderCell: ({ row }) => (
                <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                    {row?.linked_devices?.[0]?.device_value?.channels?.[9]?.current_value ? `${row?.linked_devices?.[0]?.device_value.channels[9].current_value}%` : ''}
                </MDTypography>
            )
        },
        {
            field: 'volume_gallons',
            headerName: 'Volume (Gal)',
            width: 140,
            renderCell: ({ row }) => (
                <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                    {row?.linked_devices?.[0]?.device_value?.channels?.[9]?.volume_level ? `${row?.linked_devices?.[0]?.device_value.channels[9].volume_level.toFixed(2)}` : ''}
                </MDTypography>
            )
        },
        {
            field: "last_updated",
            headerName: "Last Updated",
            width: 140,
            renderCell: ({ value }) => {
                const dateString = moment(value);
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {selectedDeviceType == 8 ? dateString.fromNow() : value}
                </MDTypography>
            }
        },
    ];

    const levelMonitorColumns = [
        {
            field: "_expand",
            headerName: " ",
            width: 44,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const row = params.row;
                if (!row.has_linked_device) return null;
                const isExpanded = expandedRows[row.id];
                const handleClick = (e) => {
                    e.stopPropagation();
                    setExpandedRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
                };
                return (
                    <Tooltip title={isExpanded ? 'Collapse linked devices' : 'Expand linked devices'}>
                        <IconButton size="small" onClick={handleClick} sx={{ color: '#fff' }}>
                            {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "convert_id",
            headerName: "ID",
            width: 50,
            renderCell: (info) => {
                return <a className='a-href' style={{ cursor: 'pointer' }} onClick={(e) => redirectUrl(e, info.value)} target="_blank" rel="noopener noreferrer">
                    {info.value}
                </a>
            },
        },
        {
            field: "alert",
            headerName: "Alert",
            headerAlign: 'center',
            align: 'center',
            renderCell: (info) => {
                return info.value ? (<i
                    title={info.value?.description}
                    className={info.value?.icon?.class_name ?? "fas fa-triangle-exclamation"}
                    onClick={(e) => showAlert(e, info.id)}
                    style={{ cursor: 'pointer', color: `${info.value?.badge?.font_color_hex ?? 'red'}`, fontSize: '1.5em' }}
                />
                ) : ''
            },
        },
        {
            field: "name",
            headerName: "Device Name",
            width: 200
        },
        {
            field: "percent_level",
            headerName: "Percent",
            renderCell: (info) => {
                return <MDBox style={{ position: "relative", display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                        {info.value}%
                    </MDTypography>
                    <StyledLinearProgress
                        variant="determinate"
                        value={typeof info.value === "number" ? info.value : 0}
                    />
                </MDBox>
            }
        },
        {
            field: "volume_level",
            headerName: "Volume",
        },
        {
            field: "ullage_volume",
            headerName: "Ullage (US gal)",
            width: 140,
            renderCell: (info) => {
                const isValue = info.value !== null && info.value !== undefined;
                return (
                    <Tooltip title={info.row.ullage_tooltip ?? ULLAGE_ESTIMATE_TOOLTIP}>
                        <span>{formatUllageNumber(info.value)}{isValue ? ' (est.)' : ''}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: "identifier",
            headerName: "Identifier",
            width: 160
        },
        {
            field: "entity",
            headerName: "Entity Name",
            width: 200
        },
        {
            field: "entity_address_name",
            headerName: "Item Name",
            width: 200
        },
        {
            field: "item",
            headerName: "Item Type",
            width: 150
        },
        {
            field: "address",
            headerName: "Address",
            width: 200
        },
        {
            field: "city",
            headerName: "City",
            width: 150
        },
        {
            field: "state",
            headerName: "State",
        },
        {
            field: "postal_code",
            headerName: "Postal Code",
        },
        {
            field: "lat",
            headerName: "Latitude",
            width: 110
        },
        {
            field: "long",
            headerName: "Longitude",
            width: 110
        },
        {
            field: "inch_level",
            headerName: "Product Level (inches)",
        },
        {
            field: "battery_voltage",
            headerName: "Battery Voltage",
        },
        {
            field: "wifi_signal",
            headerName: "Wi-Fi Signal",
        },
        // {
        //     field: "linked_devices",
        //     headerName: "Linked Devices",
        //     width: 140,
        //     renderCell: ({ row }) => {
        //         const linkedCount = row?.linked_devices?.length || row?.device_value?.linked_devices?.length || 0;
        //         const isExpanded = expandedRows[row.id];
        //         return linkedCount > 0 ? (
        //             <MDButton
        //                 onClick={(e) => handleToggleRow(e, row.id)}
        //                 variant="text"
        //                 size="small"
        //                 sx={{ color: isExpanded ? '#info.main' : '#fff' }}
        //             >
        //                 {isExpanded ? 'Hide' : 'View'} ({linkedCount})
        //             </MDButton>
        //         ) : '';
        //     }
        // },
        {
            field: "last_updated",
            headerName: "Last Updated",
            width: 140,
            renderCell: ({ value }) => {
                const dateString = moment(value);
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {selectedDeviceType == 8 ? dateString.fromNow() : value}
                </MDTypography>
            }
        },
        {
            field: "last24h_percent",
            headerName: "Last 24H Percent Change",
            renderCell: ({ value }) => {
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {value}%
                </MDTypography>
            }
        },
        {
            field: "last24h_level",
            headerName: "Last 24H Level Change",
            renderCell: ({ value }) => {
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {value} inches
                </MDTypography>
            }
        },
        {
            field: "last24h_volume",
            headerName: "Last 24H Volume Change",
            renderCell: ({ value }) => {
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {value} gal
                </MDTypography>
            }
        },
        {
            field: "ai_trend",
            headerName: "AI Trend",
            width: 120,
            renderCell: ({ value }) => {
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {value} {renderIcon("Trend", value)}
                </MDTypography>
            }
        },
        {
            field: "ai_status",
            headerName: "AI Status",
            width: 120,
            renderCell: ({ value }) => {
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {value} {renderIcon("Status", value)}
                </MDTypography>
            }
        },
    ];

    const tabletColumns = [
        {
            field: "_expand",
            headerName: " ",
            width: 44,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const row = params.row;
                if (!row.has_linked_device) return null;
                const isExpanded = expandedRows[row.id];
                const handleClick = (e) => {
                    e.stopPropagation();
                    setExpandedRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
                };
                return (
                    <Tooltip title={isExpanded ? 'Collapse linked devices' : 'Expand linked devices'}>
                        <IconButton size="small" onClick={handleClick} sx={{ color: '#fff' }}>
                            {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "convert_id",
            headerName: "ID",
            width: 50,
            renderCell: (info) => {
                return <a className='a-href' style={{ cursor: 'pointer' }} onClick={(e) => redirectUrl(e, info.value)} target="_blank" rel="noopener noreferrer">
                    {info.value}
                </a>
            },
        },
        {
            field: "alert",
            headerName: "Alert",
            headerAlign: 'center',
            align: 'center',
            renderCell: (info) => {
                return info.value ? (<i
                    title={info.value?.description}
                    className={info.value?.icon?.class_name ?? "fas fa-triangle-exclamation"}
                    onClick={(e) => showAlert(e, info.id)}
                    style={{ cursor: 'pointer', color: `${info.value?.badge?.font_color_hex ?? 'red'}`, fontSize: '1.5em' }}
                />
                ) : ''
            },
        },
        {
            field: "name",
            headerName: "Device Name",
            width: 200
        },
        {
            field: "percent_level",
            headerName: "Percent",
            renderCell: (info) => {
                const value = info.value;
                const displayValue = (typeof value === 'number' && isFinite(value)) ? Math.round(value) : null;
                return <MDBox style={{ position: "relative", display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                        {displayValue !== null ? `${displayValue}%` : '--'}
                    </MDTypography>
                    <StyledLinearProgress
                        variant="determinate"
                        value={typeof value === "number" ? Math.max(0, Math.min(100, value)) : 0}
                    />
                </MDBox>
            }
        },
        {
            field: "psi",
            headerName: "PSI",
            width: 110,
            renderCell: (info) => {
                const value = info.value;
                return (typeof value === 'number' && isFinite(value)) ? Math.round(value) : '--';
            },
        },
        {
            field: "volume_gallons",
            headerName: "Vol. (gal)",
            width: 110,
            renderCell: (info) => {
                const value = info.value;
                return (typeof value === 'number' && isFinite(value)) ? Math.round(value) : '--';
            },
        },
        {
            field: "gallons_remaining",
            headerName: "Rem. (gal)",
            width: 110,
            renderCell: (info) => {
                const value = info.value;
                return (typeof value === 'number' && isFinite(value)) ? Math.round(value) : '--';
            },
        },
        {
            field: "moment_status",
            headerName: "Status",
            width: 120,
            renderCell: (info) => {
                const status = info.value;
                const formattedStatus = typeof status === 'string' 
                    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() 
                    : status;
                return <Badge
                    color={gpsStatusBadgeColor(status)}
                    variant={'standard'}
                    badgeContent={formattedStatus || 'Unknown'}
                    style={{ marginLeft: "15px" }}
                />
            },
        },
        {
            field: "speed",
            headerName: "Speed",
            width: 110,
            renderCell: (info) => {
                const value = info.value;
                return (typeof value === 'number' && isFinite(value)) ? `${Math.round(value)} mph` : '--';
            },
        },
        {
            field: "vehicle_connected",
            headerName: "Veh. conn.",
            width: 120,
            renderCell: (info) => {
                if (info.value === true) return 'Connected';
                if (info.value === false) return 'Disconnected';
                return 'Not reported';
            },
        },
        {
            field: "active_fault_count",
            headerName: "Faults",
            width: 110,
            renderCell: (info) => {
                const value = info.value;
                return (typeof value === 'number' && isFinite(value)) ? Math.round(value) : '--';
            },
        },
        {
            field: "location",
            headerName: "Fence",
            description: "Geofence name, or reverse-geocoded location when no fence is associated",
            width: 170,
            minWidth: 150,
            renderCell: ({ row, value }) => {
                const fenceName = row.geofence_name;
                const displayValue = fenceName || "Not reported";
                return (
                    <MDTypography
                        variant="span"
                        title={displayValue}
                        sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "0.8rem", minWidth: 0 }}
                    >
                        {displayValue}
                        {row.geofence_color && (
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: row.geofence_color }} />
                        )}
                    </MDTypography>
                );
            },
        },
        {
            field: "identifier",
            headerName: "Identifier",
            width: 160
        },
        {
            field: "entity",
            headerName: "Entity Name",
            width: 200
        },
        {
            field: "entity_address_name",
            headerName: "Item Name",
            width: 200
        },
        {
            field: "item",
            headerName: "Item Type",
            width: 150
        },
        {
            field: "address",
            headerName: "Address",
            width: 200
        },
        {
            field: "city",
            headerName: "City",
            width: 150
        },
        {
            field: "state",
            headerName: "State",
        },
        {
            field: "postal_code",
            headerName: "Postal Code",
        },
        {
            field: "lat",
            headerName: "Latitude",
            width: 110
        },
        {
            field: "long",
            headerName: "Longitude",
            width: 110
        },
        {
            field: "last_updated",
            headerName: "Last Updated",
            width: 140,
            renderCell: ({ value }) => {
                const dateString = moment(value);
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {selectedDeviceType == 8 ? dateString.fromNow() : value}
                </MDTypography>
            }
        },
    ];

    const holykelColumns = [
        {
            field: "_expand",
            headerName: " ",
            width: 44,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const row = params.row;
                if (!row.has_linked_device) return null;
                const isExpanded = expandedRows[row.id];
                const handleClick = (e) => {
                    e.stopPropagation();
                    setExpandedRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
                };
                return (
                    <Tooltip title={isExpanded ? 'Collapse linked devices' : 'Expand linked devices'}>
                        <IconButton size="small" onClick={handleClick} sx={{ color: '#fff' }}>
                            {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "convert_id",
            headerName: "ID",
            width: 50,
            renderCell: (info) => {
                return <a className='a-href' style={{ cursor: 'pointer' }} onClick={(e) => redirectUrl(e, info.value)} target="_blank" rel="noopener noreferrer">
                    {info.value}
                </a>
            },
        },
        {
            field: "alert",
            headerName: "Alert",
            headerAlign: 'center',
            align: 'center',
            renderCell: (info) => {
                return info.value ? (<i
                    title={info.value?.description}
                    className={info.value?.icon?.class_name ?? "fas fa-triangle-exclamation"}
                    onClick={(e) => showAlert(e, info.id)}
                    style={{ cursor: 'pointer', color: `${info.value?.badge?.font_color_hex ?? 'red'}`, fontSize: '1.5em' }}
                />
                ) : ''
            },
        },
        {
            field: "name",
            headerName: "Device Name",
            width: 200
        },
        {
            field: "percent_level",
            headerName: "Percent",
            renderCell: (info) => {
                return <MDBox style={{ position: "relative", display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                        {info.value}%
                    </MDTypography>
                    <StyledLinearProgress
                        variant="determinate"
                        value={typeof info.value === "number" ? info.value : 0}
                    />
                </MDBox>
            }
        },
        {
            field: "psi",
            headerName: "PSI",
        },
        {
            field: "volume_level",
            headerName: "Volume",
        },
        {
            field: "ullage_volume",
            headerName: "Ullage (US gal)",
            width: 140,
            renderCell: (info) => {
                const isValue = info.value !== null && info.value !== undefined;
                return (
                    <Tooltip title={info.row.ullage_tooltip ?? ULLAGE_ESTIMATE_TOOLTIP}>
                        <span>{formatUllageNumber(info.value)}{isValue ? ' (est.)' : ''}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: "job_state",
            headerName: "Job State",
        },
        {
            field: "identifier",
            headerName: "Identifier",
            width: 160
        },
        {
            field: "entity",
            headerName: "Entity Name",
            width: 200
        },
        {
            field: "entity_address_name",
            headerName: "Item Name",
            width: 200
        },
        {
            field: "item",
            headerName: "Item Type",
            width: 150
        },
        {
            field: "address",
            headerName: "Address",
            width: 200
        },
        {
            field: "city",
            headerName: "City",
            width: 150
        },
        {
            field: "state",
            headerName: "State",
        },
        {
            field: "postal_code",
            headerName: "Postal Code",
        },
        {
            field: "lat",
            headerName: "Latitude",
            width: 110
        },
        {
            field: "long",
            headerName: "Longitude",
            width: 110
        },
        {
            field: "fluid_height",
            headerName: "Fluid Height",
        },
        {
            field: "calibrated_level",
            headerName: "Calibrated level",
        },
        {
            field: "usable_height",
            headerName: "Usable Height",
        },
        {
            field: "raw_level",
            headerName: "Raw Level",
        },
        {
            field: 'moment_status',
            headerName: 'Status',
            width: 120,
            renderCell: ({ row }) => (
                <Badge
                    color={gpsStatusBadgeColor(row?.linked_devices?.[0]?.device_value?.moment_status)}
                    variant={'standard'}
                    badgeContent={row?.linked_devices?.[0]?.device_value?.moment_status || ''}
                    style={{ marginLeft: "30px" }}
                />
            )
        },
        {
            field: 'speed',
            headerName: 'Speed',
            width: 100,
            renderCell: ({ row }) => (
                <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                    {row?.linked_devices?.[0]?.device_value?.convert_position_speed ?? ''}
                </MDTypography>
            )
        },
        {
            field: 'heading',
            headerName: 'Heading',
            width: 100,
            renderCell: ({ row }) => (
                <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                    {row?.linked_devices?.[0]?.device_value?.position_direction ?? ''}
                </MDTypography>
            )
        },
        {
            field: 'location',
            headerName: 'Location',
            width: 300,
            renderCell: ({ row }) => (
                <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                    {row?.linked_devices?.[0]?.geofences?.name ?? row?.linked_devices?.[0]?.device_value?.reverse_geocoded_address?.formatted_address ?? ''}
                </MDTypography>
            )
        },
        {
            field: "last_updated",
            headerName: "Last Updated",
            width: 140,
            renderCell: ({ value }) => {
                const dateString = moment(value);
                return <MDTypography variant="p" sx={{ fontSize: '0.8rem' }}>
                    {selectedDeviceType == 8 ? dateString.fromNow() : value}
                </MDTypography>
            }
        },
    ];

    function changeVisibility(model) {
        if (selectedDeviceType == 9) {
            setColumnVisibilityHolyKel(model)
            localStorage.setItem('columnVisibilityHolyKel', JSON.stringify(model))
        }
        if (selectedDeviceType == 8) {
            setColumnVisibilityAssetTracker(model)
            localStorage.setItem('columnVisibilityAssetTracker', JSON.stringify(model))
        }
        if (selectedDeviceType == 4) {
            setColumnVisibilityLevelMonitor(model)
            localStorage.setItem('columnVisibilityLevelMonitor', JSON.stringify(model))
        }
        if (selectedDeviceType == 10) {
            setColumnVisibilityTablet(model)
            localStorage.setItem('columnVisibilityTablet', JSON.stringify(model))
        }
    }

    function changeColumnOrder(newOrder) {
        if (selectedDeviceType == 9) {
            setColumnOrderHolyKel(newOrder)
            localStorage.setItem('columnOrderHolyKel', JSON.stringify(newOrder))
        }
        if (selectedDeviceType == 8) {
            setColumnOrderAssetTracker(newOrder)
            localStorage.setItem('columnOrderAssetTracker', JSON.stringify(newOrder))
        }
        if (selectedDeviceType == 4) {
            setColumnOrderLevelMonitor(newOrder)
            localStorage.setItem('columnOrderLevelMonitor', JSON.stringify(newOrder))
        }
        if (selectedDeviceType == 10) {
            setColumnOrderTablet(newOrder)
            localStorage.setItem('columnOrderTablet', JSON.stringify(newOrder))
        }
    }

    function fetchTable(type) {
        if (type === 4) return levelMonitorColumns
        if (type === 8) return assetTrackerColumns
        if (type === 9) return holykelColumns
        if (type === 10) return tabletColumns
        return {}
    }

    function getVisibilityModel(type) {
        if (type === 4) {
            return columnVisibilityLevelMonitor;
        }
        if (type === 8) {
            return columnVisibilityAssetTracker;
        }
        if (type === 9) {
            return columnVisibilityHolyKel;
        }
        if (type === 10) {
            return columnVisibilityTablet;
        }
    }

    function getColumnOrder(type) {
        if (type === 4) {
            return columnOrderLevelMonitor;
        }
        if (type === 8) {
            return columnOrderAssetTracker;
        }
        if (type === 9) {
            return columnOrderHolyKel;
        }
        if (type === 10) {
            return columnOrderTablet;
        }
    }

    // Reorder columns based on saved order
    function reorderColumns(columns, order) {
        if (!order || order.length === 0) {
            return columns;
        }

        // Create a map of field to column for quick lookup
        const columnMap = {};
        columns.forEach(col => {
            columnMap[col.field] = col;
        });

        // Reorder based on saved order, then append any new columns not in the order
        const reordered = [];
        order.forEach(field => {
            if (columnMap[field]) {
                reordered.push(columnMap[field]);
                delete columnMap[field];
            }
        });

        // Add any remaining columns that weren't in the saved order
        Object.values(columnMap).forEach(col => {
            reordered.push(col);
        });

        return reordered;
    }

    // Get ordered columns for display (expand column always first)
    function getOrderedColumns() {
        const columns = fetchTable(selectedDeviceType);
        const order = getColumnOrder(selectedDeviceType);
        const reordered = reorderColumns(columns, order);
        const expandCol = reordered.find((col) => col.field === '_expand');
        if (!expandCol) return reordered;
        return [expandCol, ...reordered.filter((col) => col.field !== '_expand')];
    }

    // Handle saving column order from settings panel
    function handleSaveColumnOrder(newOrder) {
        changeColumnOrder(newOrder);
    }

    const CustomGridRow = (props) => {
        const { row, expandedRows } = props;
        const { has_linked_device, linked_devices, device_type } = row;
        const isExpanded = has_linked_device && expandedRows[row.id];
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <GridRow {...props} />
                {isExpanded && (
                    <MDBox sx={{
                        width: '100%',
                        backgroundColor: '#1a2035',
                        borderLeft: '4px solid #344767',
                        pl: 1
                    }}>
                        <LinkedDeviceDetailPanel linked_devices={linked_devices} deviceType={device_type} />
                    </MDBox>
                )}
            </div>
        );
    };

    return (
        <>
            <div style={{ flexGrow: 1, flexShrink: 0, overflow: "hidden", height: '100%' }}>
                <MDBox sx={{ display: 'flex' }} justifyContent="right" alignItems="center">
                    <MDBox width="4rem" ml="auto" style={{ marginRight: "10px" }}>
                        <MDButton size="small" variant="gradient" color="dark" fullWidth onClick={() => onChangeQrModel(!qrModel)}>
                            <i className={"fas fa-qrcode"}></i>
                        </MDButton>
                    </MDBox>
                    <Tooltip title="Reorder Columns" style={{ marginRight: "10px" }}>
                        <IconButton
                            onClick={() => setSettingsPanelOpen(true)}
                            sx={{
                                color: '#fff',
                                backgroundColor: '#344767',
                                '&:hover': {
                                    backgroundColor: '#3a4f7a'
                                }
                            }}
                            size="small"
                        >
                            <ViewWeekIcon />
                        </IconButton>
                    </Tooltip>
                    <TextField
                        label="Search"
                        variant="outlined"
                        value={searchQuery}
                        onChange={(e) => onSearch(e.target.value)}
                        style={{ marginRight: "10px" }}
                    />
                </MDBox>

                <DataGrid
                    rows={rowData}
                    getRowHeight={() => 'auto'}
                    columns={getOrderedColumns()}
                    onRowSelectionModelChange={(value) => setSelectedRows(value)}
                    rowSelectionModel={selectedRows}
                    columnVisibilityModel={getVisibilityModel(selectedDeviceType)}
                    onColumnVisibilityModelChange={(newModel) =>
                        changeVisibility(newModel)
                    }
                    slots={{ row: CustomGridRow }}
                    slotProps={{ 
                        row: { expandedRows, setExpandedRows } 
                    }}
                    getRowId={(row) => row.id}
                    sx={{
                        maxHeight: '70vh',
                        color: '#fff',
                        '& .MuiDataGrid-row': {
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)'
                            }
                        }
                    }}
                />
            </div >

            <ColumnSettingsPanel
                open={settingsPanelOpen}
                onClose={() => setSettingsPanelOpen(false)}
                columns={fetchTable(selectedDeviceType)}
                columnOrder={getColumnOrder(selectedDeviceType)}
                onSave={handleSaveColumnOrder}
            />
        </>
    );
};

export default DashboardDataTable;
